import { create } from 'zustand';
import * as Sentry from '@sentry/react';
import graphqlClient from '../common/api/graphql';
import {
  GetBotByPk,
  GetUserOrganizationsLight,
  GetBotLeadsStatus,
  GetBotPrompts,
  GetOrganizationContactTags,
} from '../common/queries/queries';
import useChatStore from './chatStore';
import api from '../common/api/api';
import {
  fetchBotPromptsRest, fetchBotRest, isBotDetailRest, isBotSettingsRest,
} from '../src/utils/botSettingsApi';
import useStripeStore from './stripeStore';
import { storageLocal } from '../src/common/utils/storage';
import { auth } from '../firebaseConfig';
import { fetchBotLeadStatusesRest, isLeadStatusesRest } from '../src/utils/leadStatusesApi';
import { fetchContactTagsRest, isContactsRest } from '../src/utils/contactsApi';
import { fetchMyMembershipsRest, isOrgMembersRest } from '../src/utils/orgMembersApi';
import {
  fetchOrganizationBotsLight,
  fetchOrganizationCreditRows,
  fetchOrganizationUserRows,
  fetchUserBotRoleRows,
  fetchUserProfile,
} from '../src/utils/userStoreApi';

interface StripePaymentMethod {
  id: string;
  type: string;
  card?: {
    brand: string;
    exp_month: number;
    exp_year: number;
    last4: string;
  };
}

interface StripeSubscriptionInfo {
  periodStart: string;
  periodEnd: string;
  subscription: any;
  status: string;
  default_payment_method?: any;
  plan?: {
    product?: any;
  };
}

interface StripeFailedPayment {
  id: string;
  amount_due: number;
  status: string;
  created: number;
  // otros campos relevantes del invoice
}

interface LeadStatus {
  id: string;
  label: string;
  code: string;
  sort_order: number;
  /** Set when statuses from several bots are loaded together (org contacts view). */
  bot_id?: string;
}

interface ContactTag {
  tag: string;
  contacts_count: number;
}

interface OrganizationStripeInfo {
  activeSubscription: StripeSubscriptionInfo | null;
  pausedSubscription: any | null;
  cancelledSubscription: any | null;
  failedPayments: StripeFailedPayment[];
  paymentMethods: StripePaymentMethod[];
  allInvoices: any[];
}

type CurrentBotRole = 'admin' | 'superadmin' | 'moderator' | 'sales';

// Entry hydration (org load and route load race on first paint) shares one in-flight request per bot.
// Explicit refreshes after a save keep calling fetchBotData directly so they never reuse a pre-save read.
const botHydrations = new Map<string, Promise<void>>();
function hydrateBotOnce(botId: string, fetchBotData: (id: string) => Promise<void>): Promise<void> {
  const pending = botHydrations.get(botId) ?? fetchBotData(botId).finally(() => botHydrations.delete(botId));
  botHydrations.set(botId, pending);
  return pending;
}

export function isCurrentBotFullyHydrated(
  bot: { id?: string; prompt?: unknown; default_prompt_id?: unknown } | null | undefined,
): boolean {
  if (!bot?.id) return false;
  return 'prompt' in bot && 'default_prompt_id' in bot;
}

interface UserStoreState {
  user: any;
  loading: boolean;
  loadingNewOrganization: boolean;
  organizations: any;
  currentOrganization: any;
  cancelledSubscription: any;
  bots: any;
  currentBot: any;
  isChangingOrganization: boolean;
  isFetchingOrganizations: boolean;
  changeCurrentBot: (bot: any) => Promise<void>;
  setCurrentBotFromRoute: (botId: string) => Promise<boolean>;
  fetchOrganizations: (uId?: string) => Promise<any[]>;
  setCurrentOrganization: (currentOrganization: any, options?: { selectBot?: boolean }) => Promise<void>;
  refreshOrganizationBots: () => Promise<void>;
  fetchBotData: (botId: string) => Promise<void>;
  hasAssignmentsEnabled: boolean;
  fetchOrganizationCredits: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  setLoadingNewOrganization: (loadingNewOrganization: boolean) => void;
  setIsChangingOrganization: (isChangingOrganization: boolean) => void;
  initialQuestionsCategories: any;
  error: any;
  updateCurrentBot: (bot: any) => void;
  updateGHLTags: (tags: string[]) => void;
  getUserBotRole: (botId: string) => Promise<void>;
  currentBotRole: CurrentBotRole | string;
  subscriptionPaused: any;
  updateBotLimit: (botId: string, newLimit: number) => void;
  setUserData: (user: any) => Promise<void>;
  updateCurrentOrganizationValues: (key: string, value: string) => void;
  isInitialized: boolean;
  setIsInitialized: (isInitialized: boolean) => void;
  setOrganizations: (organizations: any[]) => void;
  activeSubscription: any;
  hasWhiteListEnabled: boolean;
  leadStatuses: LeadStatus[];
  leadStatusesMap: { [key: string]: string };
  fetchLeadStatuses: (botId: string) => Promise<void>;
  organizationUsers: OrganizationUser[];
  organizationSuperAdminUsers: OrganizationUser[];
  fetchOrganizationUsers: () => Promise<void>;
  subAgents: SubAgent[];
  subAgentsLoading: boolean;
  subAgentsError: string | null;
  fetchSubAgents: (botId: string) => Promise<void>;
  getSubAgentById: (agentId: string) => SubAgent | undefined;
  clearSubAgents: () => void;
  contactTags: string[];
  contactTagsWithCounts: ContactTag[];
  contactTagsLoading: boolean;
  contactTagsError: string | null;
  fetchContactTags: (organizationId: string) => Promise<void>;
  logout: () => Promise<void>;
}

interface OrganizationUser {
  id: string;
  email: string;
  name: string;
  role?: string;
}

interface SubAgent {
  id: string;
  name: string;
  slug?: string;
  prompt: string;
  circumstances: string;
  auto_add_to_orchestrator: boolean;
  created_at: string;
  use_context: boolean;
}

const useUserStore = create<UserStoreState>((set, get) => ({
  user: null,
  loading: true,
  loadingNewOrganization: false,
  hasAssignmentsEnabled: false,
  hasWhiteListEnabled: false,
  organizations: [],
  currentOrganization: null,
  activeSubscription: null,
  currentBotRole: null,
  cancelledSubscription: null,
  bots: [],
  currentBot: null,
  isChangingOrganization: false,
  isFetchingOrganizations: false,
  initialQuestionsCategories: [],
  error: null,
  subscriptionPaused: null,
  isInitialized: false,
  leadStatuses: [],
  leadStatusesMap: {},
  organizationUsers: [],
  organizationSuperAdminUsers: [],
  subAgents: [],
  subAgentsLoading: false,
  subAgentsError: null,
  contactTags: [],
  contactTagsWithCounts: [],
  contactTagsLoading: false,
  contactTagsError: null,
  setIsInitialized: (isInitialized: boolean) => set({ isInitialized }),
  setOrganizations: (organizations: any[]) => set({ organizations }),
  setUserData: async (user: any) => {
    try {
      const userRoleData = await fetchUserProfile(user.uid);
      const newUser = {
        ...user,
        admin: userRoleData ? userRoleData.admin : false,
        name: userRoleData ? userRoleData.name : "",
        is_available: userRoleData ? userRoleData.is_available : false,
      };

      set({ user: newUser });

      if (user.uid) {
        const organizations = await get().fetchOrganizations(user.uid);

        if (organizations.length > 0) {
          const stripeStore = useStripeStore.getState();
          await stripeStore.fetchStripeProducts();
        }

        set({ isInitialized: true });
      }
    } catch (error) {
      set({ error: error.message, isInitialized: true });
    }
  },

  fetchLeadStatuses: async (botId: string) => {
    if (!botId) return;
    try {
      const statuses = isLeadStatusesRest()
        ? await fetchBotLeadStatusesRest(botId)
        : ((await graphqlClient.query({
          query: GetBotLeadsStatus,
          variables: { bot_id: botId },
          fetchPolicy: 'no-cache',
        })).data.bot_lead_statuses || []);
      statuses.sort((a, b) => a.sort_order - b.sort_order);

      const statusesMap = statuses.reduce((acc, status) => {
        acc[status.id] = status.label;
        return acc;
      }, {});
      set({ leadStatuses: statuses, leadStatusesMap: statusesMap });
    } catch (error) {
      console.error('Error fetching lead statuses:', error);
      set({ leadStatuses: [], leadStatusesMap: {} });
    }
  },

  fetchOrganizationUsers: async () => {
    const currentBot = get().currentBot;
    const currentBotRole = get().currentBotRole;
    // The org view (/dashboard/contacts) has no bot selected; bot pages share its org.
    const organizationId = get().currentOrganization?.id || currentBot?.organization_id;
    if (!organizationId) return;

    try {
      const rows = await fetchOrganizationUserRows(organizationId);

      // Usuarios regulares (sin superadmins)
      const allowedRoles = ['sales', 'moderator', 'admin'];
      const regularUsers: OrganizationUser[] = rows
        .filter(item => allowedRoles.includes(item.rol) && item.active !== false && item.user)
        .map(item => ({
          id: item.user.id,
          email: item.user.email,
          name: item.user.name,
          role: item.rol
        }));

      // Eliminar duplicados por elemento .user.id
      const uniqueRegularUsers = regularUsers.filter((user, index, self) =>
        index === self.findIndex((t) => t.id === user.id)
      );

      // Usuarios superadmin (solo si el usuario actual es superadmin)
      let superAdminUsers: OrganizationUser[] = [];
      if (currentBotRole === 'superadmin') {
        superAdminUsers = rows
          .filter(item => item.rol === 'superadmin' && item.active !== false && item.user)
          .map(item => ({
            id: item.user.id,
            email: item.user.email,
            name: item.user.name,
            role: item.rol
          }));

        // Eliminar duplicados
        superAdminUsers = superAdminUsers.filter((user, index, self) =>
          index === self.findIndex((t) => t.id === user.id)
        );
      }

      set({
        organizationUsers: uniqueRegularUsers,
        organizationSuperAdminUsers: superAdminUsers
      });
    } catch (error) {
      console.error('Error fetching organization users:', error);
      set({
        organizationUsers: [],
        organizationSuperAdminUsers: []
      });
    }
  },

  fetchOrganizationCredits: async () => {
    try {
      const response = { data: await fetchOrganizationCreditRows(get().currentOrganization.id) };

      if (!response.data.organization_credits || response.data.organization_credits.length === 0) {
        console.error('No organization credits found for org:', get().currentOrganization.id);
        return;
      }

      const totalCredits = response.data.organization_credits[0].total_credits;
      const subscriptionCredits = response.data.organization_credits[0].subscription_credits;
      const purchasedCredits = response.data.organization_credits[0].purchased_credits;

      // Check if auto-recharge configuration exists
      const hasAutoRecharge = response.data.organization_autorecharge?.length > 0;

      set({
        currentOrganization:
        {
          ...get().currentOrganization,
          total_credits: totalCredits,
          subscription_credits: subscriptionCredits,
          purchased_credits: purchasedCredits,
          has_autorecharge_config: hasAutoRecharge
        }
      });
    } catch (error) {
      console.error('Error fetching organization credits:', error);
    }
  },

  setCurrentOrganization: async (currentOrganization: any, options?: { selectBot?: boolean }) => {
    try {
      const shouldSelectBot = options?.selectBot ?? false;
      set({ loading: true, organizationUsers: [], organizationSuperAdminUsers: [], subAgents: [] });

      // Limpiar estado del chatStore al cambiar organización
      const chatStore = useChatStore.getState();
      chatStore.clearChats();
      chatStore.setCurrentChat(null, '', '', false, '');
      chatStore.setMessages([]);
      chatStore.clearKeywordSearch();

      // Guardar preferencia de organización
      storageLocal.setPreferredOrgId(currentOrganization.id);

      // Always fetch bots for the selected organization (organizations come without bots from GetUserOrganizationsLight)
      let bots: any[] = [];
      try {
        const fetchedBots = await fetchOrganizationBotsLight(currentOrganization.id);
        if (fetchedBots) {
          bots = fetchedBots;
          const nullBots = bots.filter((b: any) => !b);
          if (nullBots.length > 0) {
            Sentry.captureMessage('Null bots received from GetOrganizationBotsLight', {
              level: 'error',
              tags: { store: 'userStore', method: 'setCurrentOrganization' },
              extra: {
                orgId: currentOrganization.id,
                orgRol: currentOrganization.rol,
                orgBotIds: currentOrganization.bot_ids,
                totalBots: bots.length,
                nullCount: nullBots.length,
                validBotIds: bots.filter(Boolean).map((b: any) => b.id),
              },
            });
            bots = bots.filter(Boolean);
          }
        }
      } catch (error) {
        console.error('[setCurrentOrganization] Error fetching bots:', error);
      }

      Sentry.addBreadcrumb({
        category: 'store.bots',
        message: 'Bots fetched for organization',
        level: 'info',
        data: {
          orgId: currentOrganization.id,
          orgRol: currentOrganization.rol,
          orgBotIds: currentOrganization.bot_ids,
          botsCount: bots.length,
          botIds: bots.map((b: any) => b?.id),
        },
      });

      const filteredBots = !currentOrganization.bot_ids
        ? bots
        : bots.filter((b: any) => currentOrganization.bot_ids.includes(b.id));

      if (shouldSelectBot) {
        // Seleccionar bot para la organización actual
        let selectedBot = null;

        // Prefer first completed onboarding bot; fallback to latest in list.
        if (filteredBots?.length > 0) {
          const firstCompleteBot = filteredBots.find((bot: any) => bot.onboarding_complete);
          if (firstCompleteBot) {
            selectedBot = firstCompleteBot;
          } else {
            selectedBot = filteredBots[filteredBots.length - 1];
          }
        }

        // Actualizar estado global con bot seleccionado
        set(state => ({
          loading: true,
          currentOrganization: { ...currentOrganization, bots: filteredBots },
          bots: filteredBots || [],
          currentBot: selectedBot || null,
          hasAssignmentsEnabled: selectedBot?.settings?.modules?.includes('sales_assignments') || false,
          hasWhiteListEnabled: selectedBot?.settings?.config?.check_whitelisted_users || false
        }));

        // Hydrate currentBot with full data and update related data
        if (selectedBot) {
          await hydrateBotOnce(selectedBot.id, get().fetchBotData);
          await get().getUserBotRole(selectedBot.id);
          await get().fetchLeadStatuses(selectedBot.id);
          await get().fetchSubAgents(selectedBot.id);
          await get().fetchContactTags(selectedBot.organization_id);
        }
      } else {
        // En rutas de organización no precargar datos de bot.
        set(state => ({
          loading: true,
          currentOrganization: { ...currentOrganization, bots: filteredBots },
          bots: filteredBots || [],
          currentBot: null,
          currentBotRole: currentOrganization.rol || null,
          hasAssignmentsEnabled: false,
          hasWhiteListEnabled: false,
          leadStatuses: [],
          leadStatusesMap: {},
          subAgents: [],
          subAgentsLoading: false,
          subAgentsError: null,
          contactTags: [],
          contactTagsWithCounts: [],
          contactTagsLoading: false,
          contactTagsError: null,
        }));
      }

      const stripeStore = useStripeStore.getState();
      const stripePromise = currentOrganization.customer_id
        ? stripeStore.fetchOrganizationStripeInfo(currentOrganization.customer_id, currentOrganization.id)
        : Promise.resolve();
      const creditsPromise = get().fetchOrganizationCredits();
      await Promise.all([stripePromise, creditsPromise]);

      set({ loading: false });

    } catch (error) {
      console.error('[setCurrentOrganization] Error:', error);
      set({
        loading: false,
        error: error.message,
        currentOrganization,
        bots: [],
        currentBot: null
      });
    }
  },

  setLoading: async (loading: boolean) => {
    set({ loading });
  },

  setLoadingNewOrganization: async (val: boolean) => {
    set({ loadingNewOrganization: val });
  },

  setIsChangingOrganization: (isChangingOrganization: boolean) => {
    set({ isChangingOrganization });
  },

  getUserBotRole: async (botId: string) => {
    try {
      const roleRows = await fetchUserBotRoleRows(get().user.uid, get().currentOrganization.id, botId);
      if (roleRows.length > 0) {
        set({ currentBotRole: roleRows[0].rol });
      } else {
        set({ currentBotRole: get().currentOrganization.rol });
      }
    } catch (error) {
      // @ts-ignore
      set({ error: error.message, loading: false });
    }
  },

  changeCurrentBot: async (bot: any) => {
    if (bot) {
      const chatStore = useChatStore.getState();
      const previousBotId = get().currentBot?.id;
      chatStore.setOffset(0);  // Reset to 0, not 20
      chatStore.setHasMore(true);

      if (previousBotId && previousBotId !== bot.id) {
        // Prevent stale sessions/chats from a previous bot from driving redirects.
        await chatStore.clearChats(true);
        chatStore.setMessages([]);
        useChatStore.setState({ currentChat: null, newChat: null });
      }

      if (bot.organization_id === get().currentOrganization?.id) {
        // Hydrate currentBot with full data (GetBotByPk) for pages that need it (settings, flows, etc.)
        await hydrateBotOnce(bot.id, get().fetchBotData);

        await get().getUserBotRole(bot.id);
        get().fetchLeadStatuses(bot.id);
        await get().fetchSubAgents(bot.id);
        get().fetchContactTags(bot.organization_id);
        get().fetchOrganizationUsers();
      }
    }
  },

  setCurrentBotFromRoute: async (botId: string) => {
    if (!botId) return false;

    const currentOrganization = get().currentOrganization;
    if (!currentOrganization?.id) return false;

    if (String(get().currentBot?.id) === String(botId)) {
      if (!isCurrentBotFullyHydrated(get().currentBot)) {
        await hydrateBotOnce(botId, get().fetchBotData);
      }
      return true;
    }

    const availableBots = currentOrganization?.bots?.length > 0
      ? currentOrganization.bots
      : get().bots;

    const routeBot = availableBots.find((bot: any) => String(bot.id) === String(botId));
    if (!routeBot) return false;

    set({
      currentBot: routeBot,
      hasAssignmentsEnabled: routeBot.settings?.modules?.includes('sales_assignments') || false,
      hasWhiteListEnabled: routeBot.settings?.config?.check_whitelisted_users || false,
    });

    await get().getUserBotRole(routeBot.id);
    get().fetchLeadStatuses(routeBot.id);
    await get().fetchSubAgents(routeBot.id);
    get().fetchContactTags(routeBot.organization_id);
    get().fetchOrganizationUsers();
    // Hydrate currentBot with full data for pages that need it (settings, flows, etc.)
    hydrateBotOnce(routeBot.id, get().fetchBotData);
    return true;
  },

  fetchBotData: async (botId: string) => {
    try {
      const bot = isBotDetailRest()
        ? await fetchBotRest(botId)
        : (await graphqlClient.query({
          query: GetBotByPk,
          variables: { id: botId },
          fetchPolicy: 'no-cache',
        })).data.bots_by_pk;
      set({
        currentBot: bot,
        hasAssignmentsEnabled: bot?.settings?.modules?.includes('sales_assignments') || false,
        hasWhiteListEnabled: bot?.settings?.config?.check_whitelisted_users || false
      });
    } catch (error) {
      // @ts-ignore
      set({ error: error.message, loading: false });
    }
  },

  updateCurrentBot: async (bot: any) => {
    try {
      set({
        currentBot: bot,
        hasAssignmentsEnabled: bot?.settings?.modules?.includes('sales_assignments') || false,
        hasWhiteListEnabled: bot?.settings?.config?.check_whitelisted_users || false
      });
      if (bot) {
        get().getUserBotRole(bot.id);
      } else {
        set({ currentBotRole: get().currentOrganization?.rol || null });
      }
    } catch (error) {
      set({ error: error.message, loading: false });
    }
  },

  updateBotLimit: async (botId: string, newLimit: number) => {
    const bot = get().bots.find(bot => bot.id === botId);
    if (bot) {
      bot.limit = newLimit;
      get().updateCurrentBot(bot);
    }
  },

  refreshOrganizationBots: async () => {
    const currentOrganization = get().currentOrganization;
    if (!currentOrganization?.id) {
      return;
    }

    try {
      const fetchedBots = ((await fetchOrganizationBotsLight(currentOrganization.id)) || []).filter(Boolean);
      const filteredBots = !currentOrganization.bot_ids
        ? fetchedBots
        : fetchedBots.filter((bot: { id: string }) => currentOrganization.bot_ids.includes(bot.id));

      const existingCurrentBot = get().currentBot;
      const currentBotId = existingCurrentBot?.id;
      const lightCurrentBot = currentBotId
        ? filteredBots.find((bot: { id: string }) => bot.id === currentBotId)
        : null;

      let refreshedCurrentBot = existingCurrentBot;
      if (currentBotId && existingCurrentBot?.id === currentBotId) {
        if (lightCurrentBot) {
          refreshedCurrentBot = { ...existingCurrentBot, ...lightCurrentBot };
        }
      } else if (lightCurrentBot) {
        refreshedCurrentBot = lightCurrentBot;
      }

      set((state) => ({
        bots: filteredBots,
        currentOrganization: state.currentOrganization
          ? { ...state.currentOrganization, bots: filteredBots }
          : state.currentOrganization,
        currentBot: refreshedCurrentBot,
      }));
    } catch (error) {
      console.error('[refreshOrganizationBots] Error:', error);
    }
  },

  fetchOrganizations: async (uId?: string): Promise<any[]> => {
    if (get().isFetchingOrganizations) {
      return get().organizations || [];
    }
    const resolvedUserId = uId || get().user?.uid;
    if (!resolvedUserId) return [];

    set({ isFetchingOrganizations: true });
    try {
      const fromHasura = async () => (await graphqlClient.query({
        query: GetUserOrganizationsLight,
        variables: { user_id: resolvedUserId },
        fetchPolicy: 'no-cache',
      })).data.users_organizations;
      // Login and the menu hang on this read: a REST failure falls back to Hasura.
      const rawOrgs = isOrgMembersRest() && resolvedUserId === auth.currentUser?.uid
        ? await fetchMyMembershipsRest().catch((error) => {
          Sentry.captureException(error, { tags: { store: 'userStore', method: 'fetchOrganizations' } });
          return fromHasura();
        })
        : await fromHasura();
      const nullOrgEntries = rawOrgs.filter((o: any) => !o?.organization?.id);
      if (nullOrgEntries.length > 0) {
        Sentry.captureMessage('Null/malformed organizations from GetUserOrganizationsLight', {
          level: 'error',
          tags: { store: 'userStore', method: 'fetchOrganizations' },
          extra: {
            userId: resolvedUserId,
            totalRaw: rawOrgs.length,
            nullCount: nullOrgEntries.length,
            nullEntries: nullOrgEntries.map((o: any) => ({ rol: o?.rol, bot_id: o?.bot_id, hasOrg: !!o?.organization })),
          },
        });
      }
      const mappedOrgs = rawOrgs
        .filter((org: any) => org?.organization?.id)
        .map((org: any) => ({
          ...org.organization,
          rol: org.rol,
          bot_id: org.bot_id,
          members_count: org.organization?.users_organizations_aggregate?.aggregate?.count ?? 0,
        }));

      const orgMap = new Map<string, any>();
      for (const org of mappedOrgs) {
        if (orgMap.has(org.id)) {
          const existing = orgMap.get(org.id);
          if (org.bot_id === null) {
            existing.bot_ids = null;
          } else if (existing.bot_ids !== null) {
            if (!existing.bot_ids.includes(org.bot_id)) {
              existing.bot_ids.push(org.bot_id);
            }
          }
        } else {
          orgMap.set(org.id, {
            ...org,
            bot_ids: org.bot_id === null ? null : [org.bot_id],
          });
        }
      }
      const allOrganizations = Array.from(orgMap.values()).map(({ bot_id: _b, ...rest }) => rest);

      Sentry.addBreadcrumb({
        category: 'store.organizations',
        message: 'Organizations fetched',
        level: 'info',
        data: {
          total: allOrganizations.length,
          filtered: rawOrgs.length - allOrganizations.length,
          roles: allOrganizations.map((o: any) => o.rol),
        },
      });

      set({ organizations: allOrganizations });

      if (allOrganizations.length > 0) {
        // Solo cambiar la organización si no hay una selección en progreso
        const currentOrg = get().currentOrganization;
        if (!currentOrg) {
          let targetOrg = null;

          // Try localStorage organization preference first
          if (!targetOrg) {
            const preferredOrgId = storageLocal.getPreferredOrgId();
            targetOrg = preferredOrgId
              ? allOrganizations.find(org => org.id === preferredOrgId)
              : null;
          }

          // If still no target, use first organization
          if (!targetOrg) {
            targetOrg = allOrganizations[0];
          }

          await get().setCurrentOrganization(targetOrg);
        } else {
          // Actualizar los bots de la organización actual
          const updatedCurrentOrg = allOrganizations.find(org => org.id === currentOrg.id);
          if (updatedCurrentOrg) {
            set(state => ({
              ...state,
              currentOrganization: { ...state.currentOrganization, ...updatedCurrentOrg },
            }));
          }
        }
      }

      return allOrganizations;
    } catch (error) {
      console.error('[fetchOrganizations] Error:', error);
      return [];
    } finally {
      set({ isFetchingOrganizations: false });
    }
  },

  updateGHLTags: async (tags: string[]) => {
    // Endpoint ghl/update_tags removed - function kept for backwards compatibility but does nothing
    return;
  },

  updateCurrentOrganizationValues: async (key: string, value: string) => {
    set({ currentOrganization: { ...get().currentOrganization, [key]: value } });
  },

  fetchSubAgents: async (botId: string) => {
    if (!botId) return;
    // Sales role has no Hasura permission on bot_prompts; skip the query entirely.
    if (get().currentBotRole === 'sales') {
      set({ subAgents: [], subAgentsLoading: false, subAgentsError: null });
      return;
    }
    try {
      set({ subAgentsLoading: true, subAgentsError: null });
      const prompts = isBotSettingsRest()
        ? await fetchBotPromptsRest(botId)
        : (await graphqlClient.query({
          query: GetBotPrompts,
          variables: { bot_id: botId },
          fetchPolicy: 'no-cache',
        })).data?.bot_prompts;

      if (prompts) {
        const subAgents: SubAgent[] = prompts.map((prompt: any) => ({
          id: prompt.id,
          name: prompt.name,
          slug: prompt.slug ?? undefined,
          prompt: prompt.prompt,
          circumstances: prompt.circumstances,
          auto_add_to_orchestrator: prompt.auto_add_to_orchestrator,
          created_at: prompt.created_at,
          use_context: prompt.use_context
        }));
        set({ subAgents, subAgentsLoading: false });
      }
    } catch (error) {
      console.error('Error fetching sub-agents:', error);
      set({
        subAgentsError: error.message || 'Error fetching sub-agents',
        subAgentsLoading: false,
        subAgents: []
      });
    }
  },

  getSubAgentById: (agentId: string) => {
    return get().subAgents.find(agent => agent.id === agentId);
  },

  clearSubAgents: () => {
    set({ subAgents: [], subAgentsLoading: false, subAgentsError: null });
  },

  fetchContactTags: async (organizationId: string) => {
    if (!organizationId) return;
    try {
      set({ contactTagsLoading: true, contactTagsError: null });
      const rows = isContactsRest()
        ? await fetchContactTagsRest(organizationId)
        : (await graphqlClient.query({
          query: GetOrganizationContactTags,
          variables: { organization_id: organizationId },
          fetchPolicy: 'cache-first',
        })).data?.get_organization_contact_tags;

      if (rows) {
        const tags = ["No tags", ...rows.map((item: any) => item.tag)];
        const tagsWithCounts = rows.map((item: any) => ({
          tag: item.tag,
          contacts_count: item.contacts_count
        }));
        set({
          contactTags: tags,
          contactTagsWithCounts: tagsWithCounts,
          contactTagsLoading: false
        });
      }
    } catch (error) {
      console.error('Error fetching contact tags:', error);
      set({
        contactTagsError: error.message || 'Error fetching contact tags',
        contactTagsLoading: false,
        contactTags: [],
        contactTagsWithCounts: []
      });
    }
  },

  logout: async () => {
    try {
      // Sign out from Firebase
      await auth.signOut();

      // Clear all storage
      storageLocal.clearAll();

      // Reset store state to initial values
      set({
        user: null,
        loading: true,
        loadingNewOrganization: false,
        hasAssignmentsEnabled: false,
        hasWhiteListEnabled: false,
        organizations: [],
        currentOrganization: null,
        activeSubscription: null,
        currentBotRole: null,
        cancelledSubscription: null,
        bots: [],
        currentBot: null,
        isChangingOrganization: false,
        initialQuestionsCategories: [],
        error: null,
        subscriptionPaused: null,
        isInitialized: false,
        leadStatuses: [],
        leadStatusesMap: {},
        organizationUsers: [],
        organizationSuperAdminUsers: [],
        subAgents: [],
        subAgentsLoading: false,
        subAgentsError: null,
        contactTags: [],
        contactTagsWithCounts: [],
        contactTagsLoading: false,
        contactTagsError: null,
      });

      // Clear chat store as well
      const chatStore = useChatStore.getState();
      chatStore.setOffset(0);
      chatStore.setHasMore(true);

    } catch (error) {
      console.error('Error during logout:', error);
      throw error;
    }
  },
}));

export default useUserStore;
